<template>
  <div
    class="phone-input-box"
    :class="{ 'is-focused': isFocused, 'is-disabled': disabled }"
  >
    <el-select
      v-model="areaCode"
      class="area-select"
      :disabled="disabled"
      @change="handleAreaChange"
    >
      <el-option
        v-for="item in areaCodeList"
        :key="item.value"
        :label="item.label"
        :value="item.value"
      >
        <span>{{ item.name }}</span>
        <span class="option-code">{{ item.value }}</span>
      </el-option>
    </el-select>

    <div class="divider" />

    <input
      v-model="phoneNumber"
      class="phone-input"
      type="text"
      maxlength="15"
      :placeholder="placeholder"
      :disabled="disabled"
      @input="handleInput"
      @focus="isFocused = true"
      @blur="handleBlur"
    >

    <div
      v-if="$slots.append"
      class="append-slot"
    >
      <slot name="append" />
    </div>
  </div>
</template>

<script setup>
import { ref, watch, reactive } from 'vue'

const props = defineProps({
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: '请输入手机号码' },
  disabled: { type: Boolean, default: false }
})

const emit = defineEmits(['update:modelValue', 'change', 'blur'])

const areaCode = ref('+86')
const phoneNumber = ref('')
const isFocused = ref(false)

const areaCodeList = reactive([
  { name: '中国大陆', value: '+86', label: '+86' },
  { name: '中国香港', value: '+852', label: '+852' },
  { name: '中国澳门', value: '+853', label: '+853' },
  { name: '中国台湾', value: '+886', label: '+886' },
])

// 按区号列表做前缀匹配，长区号优先（避免 +86 吃掉 +852/+853/+886 的首位）
// 列表内未知区号兜底：用正则提取 1-4 位数字作为区号，动态加入列表
const parseValue = (val) => {
  if (!val) {
    areaCode.value = '+86'
    phoneNumber.value = ''
    return
  }
  if (val.startsWith('+')) {
    const matched = [...areaCodeList]
      .sort((a, b) => b.value.length - a.value.length)
      .find(item => val.startsWith(item.value))
    if (matched) {
      areaCode.value = matched.value
      phoneNumber.value = val.substring(matched.value.length)
      return
    }
    // 未知区号：正则提取，动态加入列表
    const fallback = val.match(/^(\+\d{1,4})(\d*)$/)
    if (fallback) {
      const code = fallback[1]
      if (!areaCodeList.some(item => item.value === code)) {
        areaCodeList.push({ name: '其他', value: code, label: code })
      }
      areaCode.value = code
      phoneNumber.value = fallback[2]
      return
    }
  }
  areaCode.value = '+86'
  phoneNumber.value = val
}

watch(
  () => props.modelValue,
  (newVal) => {
    const current = `${areaCode.value}${phoneNumber.value}`
    if (newVal !== current) parseValue(newVal)
  },
  { immediate: true }
)

const emitValue = () => {
  if (!phoneNumber.value) {
    emit('update:modelValue', '')
    emit('change', '')
  } else {
    const val = `${areaCode.value}${phoneNumber.value}`
    emit('update:modelValue', val)
    emit('change', val)
  }
}

const handleInput = () => {
  phoneNumber.value = phoneNumber.value.replace(/[^\d]/g, '')
  emitValue()
}

const handleAreaChange = () => emitValue()

const handleBlur = (e) => {
  isFocused.value = false
  emit('blur', e)
}
</script>

<script>
export default { name: 'PhoneInput' }
</script>

<style scoped>
.phone-input-box {
  display: flex;
  align-items: center;
  width: 100%;
  height: 40px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  background-color: #fff;
  transition: border-color 0.2s;
  box-sizing: border-box;
  overflow: hidden;
}

.phone-input-box:hover:not(.is-disabled) {
  border-color: #c0c4cc;
}

.phone-input-box.is-focused {
  border-color: #206ef4;
  box-shadow: 0 0 0 1px #206ef4 inset;
}

.phone-input-box.is-disabled {
  background-color: #f5f7fa;
  cursor: not-allowed;
}

/* 区号下拉框：去掉所有内置样式，仅保留内容 */
.area-select {
  flex-shrink: 0;
  width: 90px;
}

.area-select :deep(.el-select__wrapper),
.area-select :deep(.el-input__wrapper) {
  box-shadow: none !important;
  border: none !important;
  background: transparent !important;
  padding: 0 4px 0 12px;
  height: 100%;
  cursor: pointer;
}

.area-select :deep(.el-select__wrapper.is-focused),
.area-select :deep(.el-input__wrapper.is-focus) {
  box-shadow: none !important;
}

.area-select :deep(.el-select__placeholder),
.area-select :deep(.el-select__selected-item),
.area-select :deep(.el-input__inner) {
  font-size: 14px;
  color: #333;
  text-align: center;
}

.divider {
  flex-shrink: 0;
  width: 1px;
  height: 22px;
  background-color: #dcdfe6;
}

/* 原生 input：完全贴合系统风格 */
.phone-input {
  flex: 1;
  height: 100%;
  padding: 0 12px;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: #333;
  min-width: 0;
}

.phone-input::placeholder {
  color: #c0c4cc;
  font-size: 14px;
}

.phone-input:disabled {
  cursor: not-allowed;
  color: #c0c4cc;
}

/* append 插槽（获取验证码等） */
.append-slot {
  flex-shrink: 0;
  height: 100%;
  display: flex;
  align-items: center;
  border-left: 1px solid #dcdfe6;
}
</style>
